import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Skeleton, Stack } from '@chakra-ui/react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { MdFullscreenExit } from 'react-icons/md';
import { Line, ReferenceLine, LineChart as RLineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { HorizontalBarsIcon } from '@/assets/icons';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import { FaceliftScrollContainer } from '@/components/Atoms/FaceliftScrollContainer';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText/FaceliftText';
import { ProfileMenu } from '@/components/Molecules/ProfileMenu';
import Show from '@/components/Molecules/Show';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import { useFullscreen } from '@/hooks/useFullscreen';
import { formatChartNumber } from '@/utils';
import type { LineChartProps } from './LineChart.types';
interface TooltipPayloadItem {
  name: string;
  value: number;
  color: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
  disabledSeries: Set<string>;
  showCurrencyInTooltip?: boolean;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  disabledSeries,
  showCurrencyInTooltip = false,
}) => {
  if (!active || !payload) return null;

  // Filter out disabled series from the tooltip
  const filteredPayload = payload.filter((entry) => {
    return !disabledSeries.has(String(entry.name));
  });

  if (filteredPayload.length === 0) return null;

  return (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px"
      boxShadow="lg"
      _dark={{ bg: 'darkModeBgColor', borderColor: 'gray.600' }}
      _light={{ bg: 'white', borderColor: 'gray.200' }}
    >
      <FaceliftText
        fontSize="sm"
        fontWeight="semibold"
        mb={2}
        _dark={{ color: 'white' }}
        _light={{ color: 'gray.800' }}
      >
        {label}
      </FaceliftText>
      <Stack gap={1}>
        {filteredPayload.map((entry) => (
          <Stack
            key={`${label}-${entry.name}`}
            direction="row"
            align="center"
            gap={2}
          >
            <Box
              width={2.5}
              height={2.5}
              borderRadius="full"
              bg={entry.color}
            />
            <FaceliftText
              fontSize="xs"
              _dark={{ color: 'gray.300' }}
              _light={{ color: 'gray.600' }}
            >
              {entry.name}: {showCurrencyInTooltip ? '$' : ''}{entry.value?.toLocaleString()}
            </FaceliftText>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const LineChart: React.FC<LineChartProps> = ({
  title = '',
  data: propData,
  isLoading = false,
  thresholds,
  height = 340,
  filters = [],
  colors: customColors,
  showCurrencyInTooltip = false,
  dynamicYAxis = false,
  maxSeriesNameLength = 35,
  showTableMenu = false,
  isMenuOpen = false,
  onMenuOpenChange,
  onViewTable,
  menuItems = [],
  ...rest
}) => {
  const defaultColors = customColors || ['success', 'oceanBlue'];
  
  // Fullscreen hook
  const { chartContainerRef, isFullScreen, handleFullScreen } = useFullscreen();

  const { transformedData, series, yDomain, yTicks } = useMemo(() => {
    if (!propData || propData.length === 0) {
      return {
        transformedData: [],
        series: [],
        yDomain: [0, 'auto'] as [number, string],
        yTicks: [] as number[],
      };
    }

    const titlesSet = new Set<string>();
    propData.forEach((category) => {
      category.data.forEach((item) => {
        titlesSet.add(item.title);
      });
    });
    const titles = Array.from(titlesSet);

    const seriesConfig = titles.map((title, index) => ({
      name: title,
      color: defaultColors[index % defaultColors.length],
    }));

    let maxValue = 0;
    const flatData = propData.map((category) => {
      const dataPoint: Record<string, string | number> = {
        name: category.category,
      };
      category.data.forEach((item) => {
        dataPoint[item.title] = item.value;
        maxValue = Math.max(maxValue, item.value);
      });
      titles.forEach((title) => {
        if (!(title in dataPoint)) dataPoint[title] = 0;
      });
      return dataPoint;
    });

    // Prepend a hidden origin point so lines start at (0,0) but label is invisible
    const zeroPoint: Record<string, string | number> = { name: '.00' };
    titles.forEach((title) => {
      zeroPoint[title] = 0;
    });
    flatData.unshift(zeroPoint);

    // Match spec: fixed range and labeled ticks like 0, $2,500, $5,000, $7,500, $10,000
    let fixedMax: number;
    let ticks: number[];

    if (dynamicYAxis) {
      // Dynamic step chooser based on max value
      const pickStep = (max: number): number => {
        if (max === 0) return 1000;
        if (max <= 1000) return 250;
        if (max <= 5000) return 1000;
        if (max <= 10000) return 2500;
        if (max <= 25000) return 5000;
        if (max <= 50000) return 10000;
        if (max <= 100000) return 25000;
        return 50000;
      };

      const step = pickStep(maxValue);
      // Add padding: round up to next step + 1 extra step for headroom
      const calculatedMax = (Math.ceil(maxValue / step) + 1) * step;
      const tickCount = Math.floor(calculatedMax / step) + 1;
      ticks = Array.from({ length: tickCount }, (_, i) => i * step);
      fixedMax = calculatedMax;
    } else {
      // Original fixed approach
      fixedMax = 10000;
      ticks = Array.from({ length: 11 }, (_, i) => i * 1000);
    }

    return {
      transformedData: flatData,
      series: seriesConfig,
      yDomain: [dynamicYAxis ? -fixedMax * 0.05 : 0, fixedMax] as [number, number],
      yTicks: ticks,
    };
  }, [propData, dynamicYAxis]);

  const [disabledSeries, setDisabledSeries] = useState<Set<string>>(new Set());
  const chart = useChart({ data: transformedData, series });
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const allSeriesDisabled =
    series?.every((s) => disabledSeries.has(String(s.name))) || false;

  // Calculate smart interval for X-axis to prevent label overlap
  const xAxisInterval = useMemo(() => {
    const dataLength = chart.data.length;
    if (dataLength <= 10) return 0; // Show all labels
    if (dataLength <= 20) return 1; // Show every other label
    if (dataLength <= 40) return Math.floor(dataLength / 15); // Show ~15 labels
    return Math.floor(dataLength / 12); // Show ~12 labels max for large datasets
  }, [chart.data.length]);

  // Toggle series visibility
  const handleLegendClick = (seriesName: string) => {
    setDisabledSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesName)) {
        newSet.delete(seriesName);
      } else {
        newSet.add(seriesName);
      }
      return newSet;
    });
  };

  // Truncate long text helper
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Render custom legend
  const renderCustomLegend = () => {
    const allSeries = series || [];

    return (
      <Box
        width="100%"
        minHeight="50px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        pb={2}
      >
        <Stack
          direction="row"
          justify="center"
          align="center"
          gap={3}
          opacity={1}
          flexWrap="wrap"
        >
          {allSeries.map((seriesItem, index) => {
            const seriesName = String(seriesItem.name);
          const displayName = truncateText(seriesName, maxSeriesNameLength);
            const isDisabled = disabledSeries.has(seriesName);
            const color = chart.color(defaultColors[index % defaultColors.length] || seriesItem.color);

            return (
              <Stack
                key={seriesName || `legend-${index}`}
                direction="row"
                align="center"
                gap={1.5}
                flexShrink={0}
                cursor="pointer"
                onClick={() => handleLegendClick(seriesName)}
                opacity={isDisabled ? 0.4 : 1}
                transition="opacity 0.2s"
                _hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
              title={seriesName}
              >
                <Box width={2.5} height={2.5} borderRadius="full" bg={color} />
                <FaceliftText
                  fontSize="2xs"
                  fontWeight="medium"
                  lineHeight="tight"
                  _dark={{ color: 'gray.200' }}
                  _light={{ color: 'black' }}
                  whiteSpace="nowrap"
                  textDecoration={isDisabled ? 'line-through' : 'none'}
                >
                {displayName}
                </FaceliftText>
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  };

  // Build loading skeleton geometry with static coordinates (no animation)
  const skeleton = useMemo(() => {
    const geo = { yAxisX: 12, top: 20, bottom: 200, right: 590, lineStartX: 24 };
    const xBase = (i: number) => geo.lineStartX + i * 80;
    const toPoints = (ys: number[]) => ys.map((y, i) => ({ x: xBase(i), y }));
    // Three fixed series with slight zig-zag differences
    const seriesYs = [
      [180, 200, 140, 120, 80, 150, 100],
      [170, 150, 138, 100, 142, 162, 152],
      [190, 80, 150, 140, 155, 175, 165],
    ];
    const points = seriesYs.map(toPoints);
    const toPolyline = (pts: { x: number; y: number }[]) => pts.map((p) => `${p.x},${p.y}`).join(' ');
    const polylines = points.map(toPolyline);
    return { geo, polylines, points };
  }, []);

  // Static skeleton color + shimmer colors matching title skeleton
  const skeletonColor = isDark ? 'var(--chakra-colors-gray-700)' : 'var(--chakra-colors-gray-200)';
  const shimmer = useMemo(
    () => ({
      start: isDark ? 'var(--chakra-colors-gray-700)' : 'var(--chakra-colors-gray-200)',
      mid: isDark ? 'var(--chakra-colors-gray-600)' : 'var(--chakra-colors-gray-100)',
    }),
    [isDark],
  );

  return (
    <>
      <Show when={isLoading}>
        <Box
          width="100%"
          height={`${height}px`}
          borderRadius="5px"
          borderWidth="1px"
          borderColor={'gray.400'}
          p={3}
          pl={2}
          _dark={{ bg: 'darkModeBgColor' }}
          _light={{ bg: 'white' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          {...rest}
        >
          {/* Title and Filter Skeleton */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} ml={4} mr={4}>
          <Skeleton
            height="20px"
              width={filters.length > 0 ? "250px" : "60%"}
            maxW="240px"
              _dark={{ bg: 'gray.700' }}
              _light={{ bg: 'gray.200' }}
            />
            <Show when={filters.length > 0}>
              <Skeleton
                height={8}
                width="145px"
                borderRadius="md"
            _dark={{ bg: 'gray.700' }}
            _light={{ bg: 'gray.200' }}
          />
            </Show>
          </Box>
          <Box
            flex="1"
            px={2}
            py={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="220px"
          >
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 600 220"
              preserveAspectRatio="none"
            >
              <defs>
                <linearGradient id="shimmerGradient" x1="-600" y1="0" x2="0" y2="0" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor={shimmer.start} />
                  <stop offset="50%" stopColor={shimmer.mid} />
                  <stop offset="100%" stopColor={shimmer.start} />
                  <animateTransform attributeName="gradientTransform" type="translate" from="-600 0" to="600 0" dur="1.2s" repeatCount="indefinite" />
                </linearGradient>
              </defs>

              {/* Axes for skeleton: keep bottom baseline and left Y-axis; no extra guides */}
              <line x1={skeleton.geo.yAxisX} y1="200" x2="590" y2="200" stroke={skeletonColor} strokeWidth="1.5" opacity="0.6" />
              <line x1={skeleton.geo.yAxisX} y1="200" x2={skeleton.geo.yAxisX} y2="20" stroke={skeletonColor} strokeWidth="1.5" opacity="0.6" />
              {/* Animated line */}
              {[0, 1, 2].map((idx) => (
                <polyline
                  key={`pl-${idx}`}
                  fill="none"
                  stroke="url(#shimmerGradient)"
                  strokeWidth={idx === 0 ? 3 : 2.5}
                  strokeLinecap="round"
                  strokeLinejoin="miter"
                  points={skeleton.polylines[idx]}
                  opacity={idx === 0 ? 0.9 : idx === 1 ? 0.7 : 0.6}
                />
              ))}
              {/* Pulsing points */}
              {skeleton.points.map((series, sIdx) => (
                series.map((p, i) => (
                  <circle
                    key={`pt-${sIdx}-${i}`}
                    cx={p.x}
                    cy={p.y}
                    r={sIdx === 0 ? 3.5 : 3}
                    fill="url(#shimmerGradient)"
                    opacity={sIdx === 0 ? 0.8 : sIdx === 1 ? 0.7 : 0.6}
                  />
                ))
              ))}
            </svg>
          </Box>
        </Box>
      </Show >

      <Show when={!isLoading}>
        <Box
          ref={chartContainerRef}
          width="100%"
          height={isFullScreen ? '100vh' : `${height}px`}
          borderRadius="5px"
          borderWidth="1px"
          borderColor={'gray.400'}
          p={isFullScreen ? 8 : 3}
          pl={isFullScreen ? 8 : 2}
          _dark={{ bg: 'darkModeBgColor' }}
          _light={{ bg: 'white' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          position="relative"
          {...rest}
        >
          {/* Exit Fullscreen Button */}
          <Show when={isFullScreen}>
            <FaceliftIconButton
              position="absolute"
              top={4}
              right={4}
              zIndex={1000}
              aria-label="Exit fullscreen"
              onClick={handleFullScreen}
              size="md"
              variant="solid"
              _dark={{ bg: 'gray.700', _hover: { bg: 'gray.600' }, color: 'white' }}
              _light={{ bg: 'gray.100', _hover: { bg: 'gray.200' }, color: 'gray.800' }}
            >
              <MdFullscreenExit size={20} />
            </FaceliftIconButton>
          </Show>
          <Show when={!!title}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1} ml={4} mr={4}>
            <FaceliftText
              fontSize="md"
              fontWeight="semibold"
              _dark={{ color: 'white' }}
              _light={{ color: 'gray.800' }}
            >
              {title}
            </FaceliftText>
              <Show when={!isFullScreen}>
                <Stack direction="row" gap={2} align="center">
                  <Show when={filters.length > 0}>
                    {filters.map((filter, index) => (
                      <Box key={index} width="145px">
                        <FaceliftSelect<SelectItem, false>
                          placeholder={filter.label}
                          value={filter.defaultValue}
                          onChange={(newValue) => filter.onChange?.(newValue as SelectItem)}
                          items={filter.options}
                          isSearchable={false}
                        />
                      </Box>
                    ))}
                  </Show>
                  {/* Chart Options Menu */}
                  <Show when={showTableMenu}>
                    <ProfileMenu
                      trigger={
                        <FaceliftIconButton
                          aria-label="chart options menu"
                          size="sm"
                          variant="ghost"
                        >
                          <HorizontalBarsIcon width={16} height={15} />
                        </FaceliftIconButton>
                      }
                      isOpen={isMenuOpen}
                      onOpenChange={onMenuOpenChange || (() => {})}
                      onNavigate={(href) => {
                        if (href === '#view-fullscreen') {
                          handleFullScreen();
                        } else {
                          onViewTable?.(href);
                        }
                      }}
                      menuItems={menuItems}
                    />
                  </Show>
                </Stack>
              </Show>
            </Box>
          </Show>
          <Show when={allSeriesDisabled}>
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              zIndex={10}
            >
              <FaceliftText
                fontSize="md"
                fontWeight="semibold"
                _dark={{ color: 'gray.400' }}
                _light={{ color: 'gray.500' }}
              >
                No data found
              </FaceliftText>
            </Box>
          </Show>
          <FaceliftScrollContainer
            height="calc(100% - 50px)"
            minWidth="1300px"
            pb={0}
          >
            <Box
              position="relative"
              height="100%"
              width="100%"
              display="flex"
              flexDirection="row"
            >
              <Chart.Root height="100%" width="100%" chart={chart}>
                <RLineChart
                  data={chart.data}
                  margin={{ top: 14, right: 5, left: 20, bottom: 5 }}
                  width={1300}
                  height={Math.max(height - 120, 260)}
                >
                  {/* Remove grid lines to show only dotted thresholds */}
                  <Show when={!allSeriesDisabled}>
                    <XAxis
                      dataKey={chart.key('name')}
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        style: {
                          fill: isDark ? colors.lightGray : colors.black,
                          fontSize: '11px',
                          fontWeight: 500,
                          lineHeight: '100%',
                          letterSpacing: '1%',
                          textAlign: 'center',
                          textTransform: 'capitalize',
                        },
                      }}
                      dy={8}
                      padding={{ left: 0, right: 60 }}
                      allowDuplicatedCategory={false}
                      interval={xAxisInterval}
                      tickFormatter={(val: string) => (val === '.00' ? '' : val)}
                    />
                  </Show>
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickMargin={10}
                    tick={
                      !allSeriesDisabled
                        ? {
                          style: {
                            fill: isDark ? colors.lightGray : colors.black,
                            fontSize: '10px',
                            fontWeight: 500,
                            lineHeight: '100%',
                            letterSpacing: '1%',
                            textAlign: 'right',
                          },
                        }
                        : false
                    }
                    tickFormatter={!allSeriesDisabled ? (val: number) => (val === 0 ? '.00' : `$${formatChartNumber(val)}`) : undefined}
                    domain={!allSeriesDisabled ? yDomain : [0, 'auto']}
                    ticks={!allSeriesDisabled ? yTicks : []}
                    orientation="left"
                  />
                  <Show when={!allSeriesDisabled}>
                    <Tooltip
                      cursor={false}
                      animationDuration={100}
                      content={
                        <CustomTooltip
                          disabledSeries={disabledSeries}
                          showCurrencyInTooltip={showCurrencyInTooltip}
                        />
                      }
                    />
                  </Show>

                  {/* Dotted threshold lines: Support both fixed and dynamic thresholds */}
                  <Show when={!!thresholds}>
                    {thresholds && thresholds.map((threshold, index) => {
                      let yValue: number;
                      
                      // Handle percentage-based thresholds (e.g., "80%" of max)
                      if (typeof threshold.value === 'string' && threshold.value.includes('%')) {
                        const percentage = parseFloat(threshold.value.replace('%', '')) / 100;
                        yValue = (yDomain[1] as number) * percentage;
                      } else {
                        yValue = threshold.value as number;
                      }

                      return (
                        <ReferenceLine
                          key={`threshold-${index}`}
                          y={yValue}
                          stroke={colors.robotGrey}
                          strokeDasharray="4 4"
                          label={{
                            value: threshold.label || `Threshold ${index + 1}`,
                            position: 'right',
                            style: {
                              fill: isDark ? colors.lightGray : colors.black,
                              fontSize: '10px',
                            },
                          }}
                        />
                      );
                    })}
                  </Show>

                  {/* Default thresholds if none provided */}
                  <Show when={!thresholds}>
                  <ReferenceLine
                    y={8000}
                    stroke={colors.robotGrey}
                    strokeDasharray="4 4"
                    label={{
                      value: 'Max',
                      position: 'insideTopRight',
                      style: {
                        fill: isDark ? colors.lightGray : colors.black,
                        fontSize: '10px',
                      },
                      dx: -5,
                      dy: -20,
                    }}
                  />
                  <ReferenceLine
                    y={5000}
                    stroke={colors.robotGrey}
                    strokeDasharray="4 4"
                    label={{
                      value: 'Med',
                      position: 'insideTopRight',
                      style: {
                        fill: isDark ? colors.lightGray : colors.black,
                        fontSize: '10px',
                      },
                      dx: -5,
                      dy: -20,
                    }}
                  />
                  <ReferenceLine
                    y={2000}
                    stroke={colors.robotGrey}
                    strokeDasharray="4 4"
                    label={{
                      value: 'Min',
                      position: 'insideTopRight',
                      style: {
                        fill: isDark ? colors.lightGray : colors.black,
                        fontSize: '10px',
                      },
                      dx: -5,
                      dy: -20,
                    }}
                  />
                  </Show>

                  {chart.series.map((item, idx) => {
                    const isDisabled = disabledSeries.has(String(item.name));
                    return (
                      <Show key={String(item.name)} when={!isDisabled}>
                        <Line
                          type="linear"
                          dataKey={chart.key(String(item.name))}
                          stroke={chart.color(defaultColors[idx % defaultColors.length] || item.color)}
                          strokeWidth={3}
                          dot={false}
                          activeDot={false}
                          strokeLinecap="butt"
                          strokeLinejoin="miter"
                          connectNulls
                        />
                      </Show>
                    );
                  })}
                </RLineChart>
              </Chart.Root>
            </Box>
          </FaceliftScrollContainer>
          {renderCustomLegend()}
        </Box>
      </Show>
    </>
  );
};

export default LineChart;
